import { useState } from 'react';
import { X, Bug, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWorkItemStore } from '../../store/useWorkItemStore';
import { useAppStore } from '../../store/useAppStore';
import { feature } from '../../services/telemetry';

interface BugReportModalProps {
  /** The source work item that triggered the bug report */
  sourceItem: {
    _id: string;
    title: string;
    workItemType: string;
    parentId?: string | null;
    projectId: string;
  };
  /** The criteria text that the bug is about */
  criteriaText: string;
  /** Whether this is from acceptanceCriteria or doneWhen */
  criteriaType: 'acceptanceCriteria' | 'doneWhen';
  onClose: () => void;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: '#94a3b8' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high', label: 'High', color: '#ef4444' },
  { value: 'critical', label: 'Critical', color: '#dc2626' },
];

export default function BugReportModal({ sourceItem, criteriaText, criteriaType, onClose }: BugReportModalProps) {
  const { createItem } = useWorkItemStore();
  const { selectedProjectId } = useAppStore();
  const [loading, setLoading] = useState(false);

  const sectionLabel = criteriaType === 'acceptanceCriteria' ? 'Acceptance Criteria' : 'Done When';
  const sourceLabel = sourceItem.workItemType === 'story' ? 'Story' : 'Task';

  const [title, setTitle] = useState(`Bug: ${criteriaText.substring(0, 80)}${criteriaText.length > 80 ? '…' : ''}`);
  const [description, setDescription] = useState(
    `**Observed:** The ${sectionLabel.toLowerCase()} item "${criteriaText}" on ${sourceLabel} "${sourceItem.title}" is not functioning as expected.\n\n` +
    `**Expected:** ${criteriaText}\n\n` +
    `**Steps to reproduce:**\n1. Navigate to the work item "${sourceItem.title}"\n2. Review the ${sectionLabel.toLowerCase()} section\n3. Observe that the criteria is not met\n\n` +
    `**Impact:** [Describe who/what is affected]\n\n` +
    `**Root cause (if known):** [Technical explanation]`
  );
  const [priority, setPriority] = useState('high');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setLoading(true);
    try {
      // Parent the bug to the same parent as the source item
      const parentId = sourceItem.parentId || null;
      const projectId = sourceItem.projectId || selectedProjectId;

      await createItem({
        projectId,
        parentId,
        title: title.trim(),
        description,
        workItemType: 'bug',
        status: 'new',
        priority,
        tags: [`from-${sourceItem.workItemType}`, `criteria-bug`],
      } as any);

      toast.success('Bug report created');
      feature('workitems.bug-report');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create bug');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <Bug size={18} className="text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create Bug Report</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Source Info */}
        <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 shrink-0">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-700 dark:text-amber-300">
              <span className="font-semibold">Source:</span> {sourceLabel} — {sourceItem.title}
              <br />
              <span className="font-semibold">{sectionLabel}:</span> {criteriaText}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Bug title"
                autoFocus
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <div className="flex gap-2">
                {PRIORITY_OPTIONS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                      priority === p.value
                        ? 'text-white border-transparent'
                        : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                    style={priority === p.value ? { backgroundColor: p.color } : undefined}
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: p.color }}
                    />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={12}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-accent focus:border-transparent font-mono"
                placeholder="Describe the bug..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Bug size={14} />
              {loading ? 'Creating…' : 'Create Bug'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
